"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import type {
  Offer,
  PurchaseState,
  ShoppingListItem,
  ShoppingListPatchRequest,
  ShoppingListResponse,
} from "@bitebalance/contracts"
import { useApiClient } from "@/api/useApiClient"
import { ApiError } from "@bitebalance/contracts/client"
import { subscribeServerLanguageChanged } from "@/lib/serverLanguage"
import { useI18n, formatEurFromCents } from "@/i18n"
import { LoadingState, ErrorState, EmptyState } from "@/components/state"
import { ShoppingRow } from "@/components/shop/ShoppingRow"
import { ManualAddForm } from "@/components/shop/ManualAddForm"
import { BudgetVerdictPanel, liveVerdictIsInformative } from "@/components/shop/BudgetVerdictPanel"
import { visiblePricing } from "@/components/shop/visiblePricing"
import { buildShoppingListText } from "@/components/shop/exportText"
import { groupBySection } from "@/components/shop/sections"
import {
  allResolved,
  pantrySyncText,
  purchaseRequest,
  shopDoneRequest,
} from "@/components/shop/purchase"
import { Button } from "@/components/ui/button"
import { InfoButton } from "@/components/ui/InfoButton"
import { weekBudgetHeadline } from "@/lib/weekBudgetHeadline"

type ManualShoppingItemInput = NonNullable<ShoppingListPatchRequest["addManualItems"]>[number]
type LoadStatus = "loading" | "success" | "error"

/**
 * Shop (T13): the shopping list. The pantry moved to its own top-level page
 * (/app/pantry) in the 2026-08 Pantry batch — this page keeps a small link
 * there instead of the old embedded tab.
 */
/** The persisted row that means the same purchase as `row` after the list was re-netted (ids churn on every save; the ingredient does not). */
function sameRowIn(
  current: ShoppingListResponse,
  row: ShoppingListItem | undefined
): ShoppingListItem | undefined {
  if (!row) return undefined
  if (current.items.some(item => item.id === row.id)) return row
  return current.items.find(
    item =>
      item.manual === row.manual &&
      (row.manual
        ? item.displayName === row.displayName
        : item.canonicalIngredientId === row.canonicalIngredientId)
  )
}

export default function ShopPage() {
  const api = useApiClient()
  const { t, locale } = useI18n()
  const [status, setStatus] = useState<LoadStatus>("loading")
  const [list, setList] = useState<ShoppingListResponse | null>(null)
  const [adding, setAdding] = useState(false)
  const [exportStatus, setExportStatus] = useState<"idle" | "copied" | "shared" | "error">("idle")
  const [offers, setOffers] = useState<Offer[]>([])
  // WS3 Shop → Pantry v1: the server's own count of what a confirmation changed in the Pantry (or the error).
  const [purchaseNotice, setPurchaseNotice] = useState<{
    kind: "ok" | "error"
    text: string
  } | null>(null)
  const [confirming, setConfirming] = useState(false)
  // Budget Model v2 (ADR-052 §11.3): the shopping list's SERVER verdict is
  // the only budget truth on this page — the former client-side fallback
  // (profile budget fetch + `total > budget`) is gone.

  const load = useCallback(() => {
    setStatus("loading")
    api
      .getCurrentShoppingList()
      .then(result => {
        setList(result)
        setStatus("success")
      })
      .catch(() => setStatus("error"))
    // Offers are a QUIET enhancement (ADR-028): an empty array is the honest
    // disabled/no-match state and a fetch failure degrades to the same —
    // never an error banner, never an empty-state, just no chips.
    api
      .getOffers("list")
      .then(result => setOffers(result.offers))
      .catch(() => setOffers([]))
  }, [api])

  useEffect(() => {
    load()
  }, [load])

  // The row names are localised by the SERVER (`ingredient.names[locale]` from
  // `users.locale`), so a language switch has to re-read the list — the client
  // dictionary alone cannot translate them, and must never try.
  useEffect(() => subscribeServerLanguageChanged(load), [load])

  /**
   * A write against the persisted list. The server re-nets the rows after
   * every inventory movement (ADR-076: a Kitchen write, a meal outcome, a
   * purchase confirmed on another device), which bumps the list revision
   * and replaces the row ids — so a write from a stale page gets 409
   * STALE_REVISION. Same rule as the mobile Shop: reload, rebuild the body
   * against the fresh rows (the same ingredient, its NEW id), retry once;
   * never replay old ids, never leave the page on the stale list.
   */
  async function patch(
    build: (current: ShoppingListResponse) => Omit<ShoppingListPatchRequest, "baseRevision"> | null
  ) {
    if (!list) return
    const body = build(list)
    if (!body) return
    try {
      setList(await api.patchCurrentShoppingList({ baseRevision: list.revision, ...body }))
    } catch (err) {
      if (!(err instanceof ApiError && err.envelope.code === "STALE_REVISION")) throw err
      const fresh = await api.getCurrentShoppingList()
      setList(fresh)
      const retry = build(fresh)
      if (retry)
        setList(await api.patchCurrentShoppingList({ baseRevision: fresh.revision, ...retry }))
    }
  }

  async function handleToggle(id: string, checked: boolean) {
    try {
      await patch(current => {
        const target = sameRowIn(
          current,
          list?.items.find(item => item.id === id)
        )
        if (!target) return null // the row this tap meant is gone from the persisted list: nothing to write
        return checked ? { setCheckedItemIds: [target.id] } : { setUncheckedItemIds: [target.id] }
      })
    } catch {
      // The write did not land: the box is controlled by the persisted list,
      // so it simply stays as the server has it — never an unhandled
      // rejection, never a locally invented check.
    }
  }

  /**
   * WS3: per-row purchase confirmation / "Shop done". The server defaults a
   * bought row to the packs on the list, applies a correction as the
   * DIFFERENCE, and never lets the Pantry go negative — the page only names
   * the state and shows what the server says changed.
   */
  async function confirm(body: Parameters<typeof api.confirmShoppingPurchases>[0]) {
    setConfirming(true)
    try {
      const result = await api.confirmShoppingPurchases(body)
      setList(result)
      const text = pantrySyncText(result.pantrySync, t)
      setPurchaseNotice(text ? { kind: "ok", text } : null)
    } catch {
      setPurchaseNotice({ kind: "error", text: t("views.shop.purchaseError") })
    } finally {
      setConfirming(false)
    }
  }

  function handleConfirmPurchase(id: string, state: PurchaseState, quantity?: number) {
    if (!list) return
    void confirm(purchaseRequest(list, id, state, quantity))
  }

  function handleShopDone() {
    if (!list) return
    void confirm(shopDoneRequest(list))
  }

  async function handleAddManual(item: ManualShoppingItemInput) {
    setAdding(true)
    try {
      await patch(() => ({ addManualItems: [item] }))
    } finally {
      setAdding(false)
    }
  }

  async function handleExport() {
    if (!list) return
    const text = buildShoppingListText(
      t("views.shop.tabShopping"),
      list.items,
      t("views.shop.exportRetailerLine"),
      t,
      locale
    )
    try {
      if (navigator.share) {
        await navigator.share({ text })
        setExportStatus("shared")
        return
      }
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text)
        setExportStatus("copied")
        return
      }
      setExportStatus("error")
    } catch {
      setExportStatus("error")
    }
  }

  // Shop list clarity (2026-10-06): the rows grouped under the catalog's supermarket sections, sorted by name inside
  // a section, ticked rows sinking to the bottom of THEIR section (stable). Before the catalog carries a section at all
  // the list is the flat list it always was — the server's order, no headers (see components/shop/sections.ts).
  const grouped = groupBySection(list?.items ?? [], locale)
  // Owner invariant 2026-09-04 (one dataset per presentation): the headline
  // describes the VISIBLE rows and nothing else — `list.pricing` is the
  // server's summary of exactly those rows (the number the priced rows
  // reconcile to). A server without the block degrades to the same summary
  // computed from the rows themselves (C-043: unpriced rows skipped, never
  // €0). The live verdict (`list.budget`, a recompute against the current
  // pantry/prices) is a different dataset and renders in its own labelled
  // block below — it is never the headline.
  const pricing = visiblePricing(list ?? { items: [], pricing: undefined })
  const verdict = list?.budget
  const showLiveVerdict =
    verdict !== undefined && liveVerdictIsInformative(verdict, pricing.pricedSubtotalCents)
  // Device pass 2026-10-10 (owner decision; supersedes the 2026-09-04 "visible rows only" rule for this line):
  // the week's food value against the budget, visible at the top (Backlog PL-19), with the whole-pack checkout
  // of the visible rows second. Null when the week's value is not honestly known — then nothing shows here.
  const week = weekBudgetHeadline(verdict, pricing.complete ? pricing.pricedSubtotalCents : null, t, cents =>
    formatEurFromCents(cents, locale)
  )

  return (
    <section aria-labelledby="shop-heading" className="flex flex-col gap-6">
      <h1 id="shop-heading" className="font-display text-3xl font-bold tracking-tight text-ink">
        {t("shop.title")}
      </h1>

      {week && (list?.items.length ?? 0) > 0 && (
        <div data-testid="shop-week-headline" className="flex flex-col gap-1">
          <p className="text-xs font-semibold tracking-wide text-ink-2 uppercase">{t("views.shop.weekEyebrow")}</p>
          <p className="text-xl font-bold text-ink tabular-nums">{week.primary}</p>
          {week.secondary && <p className="text-sm text-ink-2">{week.secondary}</p>}
        </div>
      )}

      <p className="text-sm text-ink-2">
        <Link to="/app/pantry" className="underline hover:text-ink">
          {t("views.shop.pantryLink")}
        </Link>
      </p>

      {status === "loading" ? (
        <LoadingState />
      ) : status === "error" || !list ? (
        <ErrorState onRetry={load} />
      ) : (
        <div className="flex flex-col gap-4">
          {list.items.length === 0 ? (
            <EmptyState title={t("views.shop.emptyTitle")} body={t("views.shop.emptyBody")} />
          ) : (
            <>
              {/* T21 propagation, founder hierarchy 2026-09-10: WHAT TO BUY → INTERACT →
                  SUPPORTING INTELLIGENCE. This page used to put nine blocks and a dozen
                  explanatory strings in front of its first row. Nothing is deleted — the
                  estimate, the price mix, the checkout verdict, the unpriced note and the
                  retailer caveat all keep their exact words and figures inside the
                  disclosure below. The list is the object, so the list comes first. */}
              <div className="flex flex-col gap-4">
                {grouped.groups.map(group => {
                  const rows = (
                    <ul className="flex flex-col gap-2">
                      {group.rows.map(item => (
                        <ShoppingRow
                          key={item.id}
                          item={item}
                          // Deterministic match only: chips appear solely where the
                          // server matched this exact ingredient (matchedIngredientId)
                          // — no client-side guessing (ADR-028 rule 3).
                          offers={offers.filter(
                            offer =>
                              offer.matchedIngredientId !== null &&
                              offer.matchedIngredientId === item.canonicalIngredientId
                          )}
                          onToggleChecked={(id, c) => void handleToggle(id, c)}
                          onConfirmPurchase={handleConfirmPurchase}
                        />
                      ))}
                    </ul>
                  )
                  if (!grouped.showHeaders) return <div key="flat">{rows}</div>
                  const headingId = `shop-section-${group.section}`
                  return (
                    // keyed by the section, never by a row id (ids change on every save)
                    <section key={group.section} aria-labelledby={headingId} data-testid={headingId} className="flex flex-col gap-2">
                      {/* the count is visible but outside the heading, so the region is announced as "Obst & Gemüse", not "Obst & Gemüse 5" */}
                      <div className="flex items-baseline justify-between text-xs font-semibold tracking-wide text-ink-2 uppercase">
                        <h2 id={headingId}>{t(`views.shop.section.${group.section}`)}</h2>
                        <span
                          aria-hidden="true"
                          data-testid={`shop-section-count-${group.section}`}
                          className="font-normal tabular-nums"
                        >
                          {group.rows.length}
                        </span>
                      </div>
                      {rows}
                    </section>
                  )
                })}
              </div>

              {/* ---- interact: what you do with the list, under the list ---- */}
              <div
                className="flex flex-col gap-2 rounded-bb-lg border border-line bg-cream p-3"
                data-testid="shop-purchase-panel"
              >
                <p className="text-sm font-semibold text-ink">{t("views.shop.purchaseTitle")}</p>
                <p className="text-xs text-ink-2">{t("views.shop.purchaseHint")}</p>
                {allResolved(list.items) ? (
                  <p className="text-xs font-medium text-green" data-testid="shop-all-resolved">
                    {t("views.shop.shopDoneAllResolved")}
                  </p>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      className="min-h-9"
                      disabled={confirming}
                      onClick={handleShopDone}
                      data-testid="shop-done"
                    >
                      {t("views.shop.shopDoneButton")}
                    </Button>
                    <span className="text-xs text-ink-2">{t("views.shop.shopDoneHint")}</span>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="min-h-9"
                    onClick={() => void handleExport()}
                  >
                    {t("views.shop.exportButton")}
                  </Button>
                  {exportStatus === "copied" && (
                    <p role="status" className="text-sm text-green">
                      {t("views.shop.exportCopied")}
                    </p>
                  )}
                  {exportStatus === "shared" && (
                    <p role="status" className="text-sm text-green">
                      {t("views.shop.exportShared")}
                    </p>
                  )}
                </div>
                {purchaseNotice && (
                  <p
                    role="status"
                    className={`text-sm ${purchaseNotice.kind === "ok" ? "text-green" : "text-red-700"}`}
                    data-testid="shop-purchase-notice"
                  >
                    {purchaseNotice.text}
                  </p>
                )}
              </div>

              {/* ---- supporting intelligence, on demand: same numbers, same honesty labels ---- */}
              <details
                className="rounded-bb-lg border border-line bg-paper"
                data-testid="shop-cost-disclosure"
              >
                <summary className="cursor-pointer list-none px-4 py-3 text-xs font-semibold tracking-wide text-ink-2 uppercase marker:content-['']">
                  {t("views.shop.costDisclosure")}
                </summary>
                <div className="flex flex-col gap-3 border-t border-line px-4 py-3">
                  <div data-testid="shop-headline">
                    {/* Owner invariant 2: a partial priced subtotal is never
                        headlined as a complete estimate — "Priced so far" plus
                        how many rows still need a reliable price. */}
                    {pricing.complete ? (
                      <>
                        <p className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
                          {t("views.budget.completeEstimateLabel")}
                        </p>
                        <p
                          className="text-xl font-bold text-ink"
                          data-testid="shop-headline-amount"
                        >
                          {formatEurFromCents(pricing.pricedSubtotalCents, locale)}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
                          {t("views.budget.pricedSoFar")}
                        </p>
                        <p
                          className="text-xl font-bold text-ink"
                          data-testid="shop-headline-amount"
                        >
                          {formatEurFromCents(pricing.pricedSubtotalCents, locale)}
                        </p>
                        <p
                          className="flex items-center gap-1 text-xs text-ink-2"
                          data-testid="shop-needs-price"
                        >
                          <span>
                            {t("views.budget.needsPriceCount", {
                              count: pricing.unpricedRequiredCount,
                            })}
                          </span>
                          <InfoButton termId="items-without-reliable-price" />
                        </p>
                      </>
                    )}
                    <p className="text-xs text-ink-2">
                      {t("views.shop.priceMix", {
                        real: pricing.realPriceCount,
                        est: pricing.estimatedPriceCount,
                      })}
                    </p>
                  </div>
                  {showLiveVerdict && verdict && <BudgetVerdictPanel verdict={verdict} />}
                  {list.unpricedCount > 0 && (
                    // Honest basket note: how many rows the total excluded
                    // because their price is truthfully unavailable (C-043).
                    <p className="text-xs text-ink-2">
                      {t("views.shop.unpricedNote", { count: list.unpricedCount })}
                    </p>
                  )}
                  <p className="text-xs text-ink-2">{t("views.shop.exportRetailerLine")}</p>
                </div>
              </details>
            </>
          )}
          <ManualAddForm onAdd={item => void handleAddManual(item)} adding={adding} />
        </div>
      )}
    </section>
  )
}
